import { useMemo, useState } from 'react';
import { useSubjectLanguageVariants } from '@features/user';
import { Chip } from '@jotpsych/ui';
import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined';
import Close from '@mui/icons-material/Close';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import EventNoteOutlined from '@mui/icons-material/EventNoteOutlined';
import FolderZipOutlined from '@mui/icons-material/FolderZipOutlined';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import Search from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { displayCalendarDate, parseCalendarDate } from '@shared/dates/calendarDate';

import type {
  GeneratedMedicalRecordPacket,
  MedicalRecordDocumentOption,
  MedicalRecordEncounterOption,
  MedicalRecordNoteOption,
  MedicalRecordsExportSelection,
  MedicalRecordsOutputMode,
} from './types';
import { useMedicalRecordsExportSelection } from './useMedicalRecordsExportSelection';

type SourceFilter = 'all' | 'encounters' | 'documents';
type BuilderStep = 'select' | 'review' | 'ready';

export interface MedicalRecordsExportDialogProps {
  open: boolean;
  subjectName: string;
  subjectDetails?: string;
  encounters: MedicalRecordEncounterOption[];
  documents: MedicalRecordDocumentOption[];
  /** Notes hidden because they are not yet completed/signed (eligibility copy). */
  excludedNoteCount?: number;
  onClose: () => void;
  onGenerate: (selection: MedicalRecordsExportSelection) => Promise<GeneratedMedicalRecordPacket>;
  /**
   * Fetch and start the download. Rejecting (e.g. an expired/revoked link)
   * surfaces an actionable error on the ready step instead of failing silently.
   */
  onDownload: (packet: GeneratedMedicalRecordPacket) => Promise<void>;
  /** Optional analytics callbacks (no PHI — counts and modes only). */
  onStepChange?: (step: 'select' | 'review' | 'ready') => void;
  onOutputModeChange?: (mode: MedicalRecordsOutputMode) => void;
}

const steps = ['Select records', 'Review packet', 'Ready'];

// authored_at is a backend date. Preserve unexpected values verbatim rather than
// hiding them or guessing a timezone. Encounter occurred_at remains a datetime.
const formatAuthoredDate = (value: string) => {
  const date = parseCalendarDate(value);
  return date
    ? displayCalendarDate(date, 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : value;
};

const formatDate = (value: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export function MedicalRecordsExportDialog({
  open,
  subjectName,
  subjectDetails,
  encounters,
  documents,
  excludedNoteCount = 0,
  onClose,
  onGenerate,
  onDownload,
  onStepChange,
  onOutputModeChange,
}: MedicalRecordsExportDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { singular } = useSubjectLanguageVariants();
  const [step, setStep] = useState<BuilderStep>('select');
  const [query, setQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [generatedPacket, setGeneratedPacket] = useState<GeneratedMedicalRecordPacket | null>(null);
  const [outputMode, setOutputMode] = useState<MedicalRecordsOutputMode>('combined_pdf');

  const selectionState = useMedicalRecordsExportSelection(encounters, documents, outputMode);

  const normalizedQuery = query.trim().toLowerCase();
  const filteredEncounters = useMemo(
    () =>
      encounters.filter(encounter => {
        if (!normalizedQuery) return true;
        return [
          encounter.title,
          encounter.providerName,
          ...encounter.notes.flatMap(note => [note.title, note.documentType]),
        ].some(value => value.toLowerCase().includes(normalizedQuery));
      }),
    [encounters, normalizedQuery],
  );
  const filteredDocuments = useMemo(
    () =>
      documents.filter(document => {
        if (!normalizedQuery) return true;
        return [document.title, document.documentType].some(value =>
          value.toLowerCase().includes(normalizedQuery),
        );
      }),
    [documents, normalizedQuery],
  );

  const activeStep = step === 'select' ? 0 : step === 'review' ? 1 : 2;
  const hasAnyRecords = encounters.length > 0 || documents.length > 0;

  const changeStep = (next: BuilderStep) => {
    setStep(next);
    onStepChange?.(next);
  };

  const changeOutputMode = (mode: MedicalRecordsOutputMode) => {
    setOutputMode(mode);
    onOutputModeChange?.(mode);
  };

  const handleGenerate = async () => {
    setGenerationError(null);
    setIsGenerating(true);
    try {
      const packet = await onGenerate(selectionState.selection);
      setGeneratedPacket(packet);
      changeStep('ready');
    } catch (error) {
      setGenerationError(
        error instanceof Error && error.message
          ? error.message
          : 'The record packet could not be generated. Try again.',
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!generatedPacket) return;
    setDownloadError(null);
    setIsDownloading(true);
    try {
      await onDownload(generatedPacket);
    } catch (error) {
      setDownloadError(
        error instanceof Error && error.message
          ? error.message
          : 'The download could not be started. Try again.',
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isGenerating ? undefined : onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="lg"
      slotProps={{
        paper: {
          sx: {
            minHeight: { md: 760 },
            maxHeight: { md: 'calc(100vh - 48px)' },
            bgcolor: 'grey.50',
          },
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6">Export medical records</Typography>
            <Stack
              direction="row"
              spacing={0.75}
              divider={<Typography color="text.disabled">·</Typography>}
              sx={{ alignItems: 'center', flexWrap: 'wrap' }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {subjectName}
              </Typography>
              {subjectDetails && (
                <Typography variant="body2" color="text.secondary">
                  {subjectDetails}
                </Typography>
              )}
            </Stack>
          </Box>
          <Tooltip title="Close">
            <span>
              <IconButton
                aria-label="Close medical records export"
                onClick={onClose}
                disabled={isGenerating}
                size="small"
              >
                <Close />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        <Stepper activeStep={activeStep} sx={{ mt: 2, maxWidth: 640, mx: 'auto' }}>
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        {step === 'select' && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' },
              gap: 2.5,
              alignItems: 'start',
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6">Choose records</Typography>
                <Typography variant="body2" color="text.secondary">
                  Select encounters and documents from this {singular}&apos;s chart. Encounter notes
                  are included automatically and can be removed individually.
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Only completed and signed notes can be exported. Notes still being prepared,
                  reviewed, or cosigned will appear after they are completed.
                  {excludedNoteCount > 0 &&
                    ` ${excludedNoteCount} ${excludedNoteCount === 1 ? 'note is' : 'notes are'} currently unavailable.`}
                </Typography>
              </Box>

              {!hasAnyRecords && (
                <Paper variant="outlined" sx={{ p: 4 }}>
                  <Stack sx={{ alignItems: 'center', color: 'text.secondary' }} spacing={0.5}>
                    <DescriptionOutlined />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      No exportable records yet
                    </Typography>
                    <Typography variant="caption">
                      Completed encounter notes and uploaded documents will appear here.
                    </Typography>
                  </Stack>
                </Paper>
              )}

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ alignItems: { sm: 'center' } }}
              >
                <TextField
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  placeholder="Search records"
                  size="small"
                  fullWidth
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <ToggleButtonGroup
                  exclusive
                  value={sourceFilter}
                  onChange={(_event, next: SourceFilter | null) => {
                    if (next) setSourceFilter(next);
                  }}
                  size="small"
                  aria-label="Filter record sources"
                >
                  <ToggleButton value="all">All</ToggleButton>
                  <ToggleButton value="encounters">Encounters</ToggleButton>
                  <ToggleButton value="documents">Documents</ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              {hasAnyRecords && (sourceFilter === 'all' || sourceFilter === 'encounters') && (
                <RecordSection
                  icon={<EventNoteOutlined fontSize="small" />}
                  title="Encounters"
                  subtitle={`${filteredEncounters.length} available`}
                >
                  {filteredEncounters.length === 0 ? (
                    <EmptySearchResult label="No encounters match this search." />
                  ) : (
                    filteredEncounters.map(encounter => (
                      <EncounterOption
                        key={encounter.id}
                        encounter={encounter}
                        selectedNoteIds={selectionState.selectedNoteIds}
                        onToggleEncounter={selectionState.toggleEncounter}
                        onToggleNote={selectionState.toggleNote}
                      />
                    ))
                  )}
                </RecordSection>
              )}

              {hasAnyRecords && (sourceFilter === 'all' || sourceFilter === 'documents') && (
                <RecordSection
                  icon={<DescriptionOutlined fontSize="small" />}
                  title="Documents"
                  subtitle={`${filteredDocuments.length} available`}
                >
                  {filteredDocuments.length === 0 ? (
                    <EmptySearchResult label="No documents match this search." />
                  ) : (
                    filteredDocuments.map(document => (
                      <DocumentOption
                        key={document.id}
                        document={document}
                        checked={selectionState.selectedDocumentIds.has(document.id)}
                        onToggle={selectionState.toggleDocument}
                      />
                    ))
                  )}
                </RecordSection>
              )}
            </Stack>

            <PacketSummary
              fileCount={selectionState.fileCount}
              generatedNoteCount={selectionState.generatedNoteCount}
              pageCount={selectionState.pageCount}
              selectedDocumentCount={selectionState.selectedDocuments.length}
              selectedEncounterCount={selectionState.selectedEncounterCount}
              selectedNoteCount={selectionState.selectedNotes.length}
              outputMode={outputMode}
              onOutputModeChange={changeOutputMode}
            />
          </Box>
        )}

        {step === 'review' && (
          <ReviewPacket
            fileCount={selectionState.fileCount}
            generatedNoteCount={selectionState.generatedNoteCount}
            pageCount={selectionState.pageCount}
            selectedDocuments={selectionState.selectedDocuments}
            selectedNotes={selectionState.selectedNotes}
            isGenerating={isGenerating}
            generationError={generationError}
            outputMode={outputMode}
          />
        )}

        {step === 'ready' && generatedPacket && (
          <ReadyPacket packet={generatedPacket} downloadError={downloadError} />
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          justifyContent: 'space-between',
        }}
      >
        {step === 'select' && (
          <>
            <Button color="inherit" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => changeStep('review')}
              disabled={selectionState.fileCount === 0}
            >
              Review packet
            </Button>
          </>
        )}
        {step === 'review' && (
          <>
            <Button color="inherit" onClick={() => changeStep('select')} disabled={isGenerating}>
              Back to selection
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleGenerate()}
              disabled={isGenerating}
              startIcon={
                isGenerating ? (
                  <CircularProgress color="inherit" size={16} />
                ) : outputMode === 'separate_pdfs_zip' ? (
                  <FolderZipOutlined />
                ) : (
                  <PictureAsPdfOutlined />
                )
              }
            >
              {isGenerating
                ? 'Generating packet…'
                : outputMode === 'separate_pdfs_zip'
                  ? 'Generate ZIP'
                  : 'Generate PDF'}
            </Button>
          </>
        )}
        {step === 'ready' && generatedPacket && (
          <>
            <Button color="inherit" onClick={onClose} disabled={isDownloading}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleDownload()}
              disabled={isDownloading}
              startIcon={
                isDownloading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : (
                  <DownloadOutlined />
                )
              }
            >
              {isDownloading
                ? 'Preparing download…'
                : generatedPacket.outputMode === 'separate_pdfs_zip'
                  ? 'Download ZIP'
                  : 'Download PDF'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

function RecordSection({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          px: 1.5,
          py: 1.25,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      </Stack>
      <Stack divider={<Divider flexItem />}>{children}</Stack>
    </Paper>
  );
}

function EncounterOption({
  encounter,
  selectedNoteIds,
  onToggleEncounter,
  onToggleNote,
}: {
  encounter: MedicalRecordEncounterOption;
  selectedNoteIds: Set<string>;
  onToggleEncounter: (encounterId: string) => void;
  onToggleNote: (noteId: string) => void;
}) {
  const selectedCount = encounter.notes.filter(note => selectedNoteIds.has(note.id)).length;
  const checked = selectedCount === encounter.notes.length;
  const indeterminate = selectedCount > 0 && !checked;

  return (
    <Box>
      <Box sx={{ px: 1.5, py: 1.25 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
          <Checkbox
            checked={checked}
            indeterminate={indeterminate}
            onChange={() => onToggleEncounter(encounter.id)}
            slotProps={{
              input: { 'aria-label': `Select all notes from ${encounter.title}` },
            }}
            sx={{ p: 0.5, mt: -0.25 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 0.25, sm: 1 }}
              sx={{ justifyContent: 'space-between' }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {encounter.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(encounter.occurredAt)}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              spacing={0.75}
              divider={<Typography color="text.disabled">·</Typography>}
              sx={{ alignItems: 'center', flexWrap: 'wrap' }}
            >
              <Typography variant="caption" color="text.secondary">
                {encounter.providerName}
              </Typography>
              {encounter.modality && (
                <Typography variant="caption" color="text.secondary">
                  {encounter.modality === 'in_person'
                    ? 'In person'
                    : encounter.modality === 'video'
                      ? 'Video'
                      : 'Audio'}
                </Typography>
              )}
              {encounter.durationMinutes && (
                <Typography variant="caption" color="text.secondary">
                  {encounter.durationMinutes} min
                </Typography>
              )}
            </Stack>
          </Box>
          <Chip
            label={`${selectedCount}/${encounter.notes.length} notes`}
            variant="soft"
            color={selectedCount === encounter.notes.length ? 'primary' : 'default'}
            size="xSmall"
          />
        </Stack>

        <Stack
          spacing={0.5}
          sx={{
            mt: 1.25,
            ml: 2.25,
            pl: 1.5,
            borderLeft: '2px solid',
            borderColor: 'primary.100',
          }}
        >
          {encounter.notes.map(note => (
            <NoteOption
              key={note.id}
              note={note}
              checked={selectedNoteIds.has(note.id)}
              onToggle={onToggleNote}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

function NoteOption({
  note,
  checked,
  onToggle,
}: {
  note: MedicalRecordNoteOption;
  checked: boolean;
  onToggle: (noteId: string) => void;
}) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minHeight: 38 }}>
      <FormControlLabel
        control={<Checkbox checked={checked} onChange={() => onToggle(note.id)} size="small" />}
        label={
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {note.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {note.documentType} · {note.pageCount} {note.pageCount === 1 ? 'page' : 'pages'}
            </Typography>
          </Box>
        }
        sx={{ flex: 1, minWidth: 0, m: 0 }}
      />
      <Chip
        label={note.pdfSource === 'signed' ? 'Signed copy' : 'Generate PDF'}
        variant="soft"
        color={note.pdfSource === 'signed' ? 'success' : 'warning'}
        size="xSmall"
      />
    </Stack>
  );
}

function DocumentOption({
  document,
  checked,
  onToggle,
}: {
  document: MedicalRecordDocumentOption;
  checked: boolean;
  onToggle: (documentId: string) => void;
}) {
  const ineligible = document.eligible === false;
  const pageLabel = document.sourcePageRange
    ? `Pages ${document.sourcePageRange.start}–${document.sourcePageRange.end}${
        document.sourcePageRange.total ? ` of ${document.sourcePageRange.total}` : ''
      }`
    : `${document.pageCount} ${document.pageCount === 1 ? 'page' : 'pages'}`;

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: 'center', px: 1.5, py: 1.25, opacity: ineligible ? 0.55 : 1 }}
    >
      <Checkbox
        checked={checked && !ineligible}
        disabled={ineligible}
        onChange={() => onToggle(document.id)}
        slotProps={{ input: { 'aria-label': `Select ${document.title}` } }}
        sx={{ p: 0.5 }}
      />
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1,
          bgcolor: 'primary.100',
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <InsertDriveFileOutlined fontSize="small" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {document.title}
        </Typography>
        <Stack
          direction="row"
          spacing={0.75}
          divider={<Typography color="text.disabled">·</Typography>}
          sx={{ alignItems: 'center', flexWrap: 'wrap' }}
        >
          <Typography variant="caption" color="text.secondary">
            {document.documentType}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatAuthoredDate(document.authoredAt)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {pageLabel}
          </Typography>
        </Stack>
      </Box>
      {ineligible ? (
        <Tooltip
          title={
            document.ineligibleReason === 'unsupported_document_type'
              ? 'This file type cannot be exported yet.'
              : 'The original file for this record is unavailable.'
          }
        >
          <span>
            <Chip label="Unavailable" variant="soft" color="default" size="xSmall" />
          </span>
        </Tooltip>
      ) : (
        document.sourcePageRange && (
          <Chip label="Page range" variant="soft" color="info" size="xSmall" />
        )
      )}
    </Stack>
  );
}

function PacketSummary({
  fileCount,
  generatedNoteCount,
  pageCount,
  selectedDocumentCount,
  selectedEncounterCount,
  selectedNoteCount,
  outputMode,
  onOutputModeChange,
}: {
  fileCount: number;
  generatedNoteCount: number;
  pageCount: number;
  selectedDocumentCount: number;
  selectedEncounterCount: number;
  selectedNoteCount: number;
  outputMode: MedicalRecordsOutputMode;
  onOutputModeChange: (mode: MedicalRecordsOutputMode) => void;
}) {
  const isSeparatePdfs = outputMode === 'separate_pdfs_zip';

  return (
    <Paper variant="outlined" sx={{ p: 2, position: { lg: 'sticky' }, top: { lg: 0 } }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Selected packet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isSeparatePdfs
              ? `One ZIP with ${fileCount} separate PDFs, organized by encounter.`
              : 'One combined PDF, ordered by encounter date.'}
          </Typography>
        </Box>

        <Divider />

        <Box
          sx={{
            p: 1.25,
            border: '1px solid',
            borderColor: isSeparatePdfs ? 'primary.main' : 'divider',
            borderRadius: 1,
            bgcolor: isSeparatePdfs ? 'primary.50' : 'background.paper',
          }}
        >
          <FormControlLabel
            sx={{ m: 0, alignItems: 'flex-start' }}
            control={
              <Checkbox
                checked={isSeparatePdfs}
                onChange={event =>
                  onOutputModeChange(event.target.checked ? 'separate_pdfs_zip' : 'combined_pdf')
                }
                size="small"
                sx={{ pt: 0.125 }}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Download as separate PDFs (.zip)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Keep each selected note and document as its own named PDF.
                </Typography>
              </Box>
            }
          />
        </Box>

        <Stack spacing={1.25}>
          <SummaryRow label="Encounters" value={selectedEncounterCount} />
          <SummaryRow label="Notes" value={selectedNoteCount} />
          <SummaryRow label="Documents" value={selectedDocumentCount} />
          <SummaryRow label="Estimated pages" value={pageCount} />
        </Stack>

        {generatedNoteCount > 0 && (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            {generatedNoteCount} {generatedNoteCount === 1 ? 'note has' : 'notes have'} no signed
            copy. The current note content will be rendered to PDF.
          </Alert>
        )}

        <Stack
          direction="row"
          spacing={1}
          sx={{
            p: 1.25,
            borderRadius: 1,
            bgcolor: 'grey.50',
            alignItems: 'flex-start',
          }}
        >
          <LockOutlined sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25 }} />
          <Box>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
              Secure temporary file
            </Typography>
            <Typography variant="caption" color="text.secondary">
              The finished packet expires automatically. Export and download are audited.
            </Typography>
          </Box>
        </Stack>

        <Typography variant="caption" color="text.secondary">
          {fileCount === 0
            ? 'Select at least one note or document to continue.'
            : `${fileCount} ${fileCount === 1 ? 'file' : 'files'} selected`}
        </Typography>
      </Stack>
    </Paper>
  );
}

function SummaryRow({ label, value }: { label: string; value: number }) {
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
    </Stack>
  );
}

function ReviewPacket({
  fileCount,
  generatedNoteCount,
  pageCount,
  selectedDocuments,
  selectedNotes,
  isGenerating,
  generationError,
  outputMode,
}: {
  fileCount: number;
  generatedNoteCount: number;
  pageCount: number;
  selectedDocuments: MedicalRecordDocumentOption[];
  selectedNotes: MedicalRecordNoteOption[];
  isGenerating: boolean;
  generationError: string | null;
  outputMode: MedicalRecordsOutputMode;
}) {
  const isSeparatePdfs = outputMode === 'separate_pdfs_zip';

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 820, mx: 'auto' }}>
      <Box>
        <Typography variant="h6">Review the packet</Typography>
        <Typography variant="body2" color="text.secondary">
          Confirm what will be included before the secure{' '}
          {isSeparatePdfs ? 'ZIP is generated.' : 'PDF is generated.'}
        </Typography>
      </Box>

      {generationError && <Alert severity="error">{generationError}</Alert>}
      {generatedNoteCount > 0 && (
        <Alert severity="warning">
          {generatedNoteCount}{' '}
          {generatedNoteCount === 1 ? 'selected note does' : 'selected notes do'} not have a signed
          PDF. The current note content will be rendered and clearly identified in the packet
          manifest.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ p: 2, bgcolor: 'grey.50', justifyContent: 'space-between' }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {isSeparatePdfs ? 'Separate medical record PDFs' : 'Combined medical record PDF'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isSeparatePdfs
                ? `${fileCount} named PDFs · organized folders · manifest included`
                : 'Cover sheet · contents · chronological records'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <ReviewMetric label="Files" value={fileCount} />
            <ReviewMetric label="Estimated pages" value={pageCount} />
          </Stack>
        </Stack>

        <Box sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Notes
          </Typography>
          <Stack spacing={1} sx={{ mt: 0.5 }}>
            {selectedNotes.map(note => (
              <ReviewRow
                key={note.id}
                title={note.title}
                detail={`${note.documentType} · ${note.pageCount} ${note.pageCount === 1 ? 'page' : 'pages'}`}
                status={note.pdfSource === 'signed' ? 'Signed copy' : 'Generated PDF'}
                statusColor={note.pdfSource === 'signed' ? 'success' : 'warning'}
              />
            ))}
          </Stack>

          {selectedDocuments.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="overline" color="text.secondary">
                Documents
              </Typography>
              <Stack spacing={1} sx={{ mt: 0.5 }}>
                {selectedDocuments.map(document => (
                  <ReviewRow
                    key={document.id}
                    title={document.title}
                    detail={`${document.documentType} · ${document.pageCount} ${document.pageCount === 1 ? 'page' : 'pages'}`}
                    status={document.sourcePageRange ? 'Selected pages' : 'Original'}
                    statusColor="info"
                  />
                ))}
              </Stack>
            </>
          )}
        </Box>
      </Paper>

      {isGenerating && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <CircularProgress size={18} />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Building the secure record packet
              </Typography>
            </Stack>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary">
              {isSeparatePdfs
                ? 'Retrieving signed copies, rendering current notes, and packaging separate PDFs.'
                : 'Retrieving signed copies, rendering current notes, and assembling documents.'}
            </Typography>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

function ReviewMetric({ label, value }: { label: string; value: number }) {
  return (
    <Box>
      <Typography variant="h6" sx={{ textAlign: 'right' }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

function ReviewRow({
  title,
  detail,
  status,
  statusColor,
}: {
  title: string;
  detail: string;
  status: string;
  statusColor: 'success' | 'warning' | 'info';
}) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
      <PictureAsPdfOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {detail}
        </Typography>
      </Box>
      <Chip label={status} variant="soft" color={statusColor} size="xSmall" />
    </Stack>
  );
}

function ReadyPacket({
  packet,
  downloadError,
}: {
  packet: GeneratedMedicalRecordPacket;
  downloadError: string | null;
}) {
  const isSeparatePdfs = packet.outputMode === 'separate_pdfs_zip';

  return (
    <Stack
      spacing={2.5}
      sx={{ maxWidth: 620, mx: 'auto', py: { xs: 4, md: 9 }, textAlign: 'center' }}
    >
      {downloadError && (
        <Alert severity="error" sx={{ textAlign: 'left' }}>
          {downloadError}
        </Alert>
      )}
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          bgcolor: 'success.light',
          color: 'success.dark',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
        }}
      >
        <CheckCircleOutlineOutlined sx={{ fontSize: 40 }} />
      </Box>
      <Box>
        <Typography variant="h5">Record packet is ready</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {isSeparatePdfs
            ? 'The ZIP of separate PDFs is encrypted and ready to download.'
            : 'The combined PDF is encrypted and ready to download.'}
        </Typography>
      </Box>
      <Paper variant="outlined" sx={{ p: 2, textAlign: 'left' }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 1,
              bgcolor: 'primary.100',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isSeparatePdfs ? <FolderZipOutlined /> : <PictureAsPdfOutlined />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
              {packet.fileName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {packet.sizeLabel} · Available until {packet.expiresAtLabel}
            </Typography>
          </Box>
          <Chip
            label={isSeparatePdfs ? 'Secure ZIP' : 'Secure PDF'}
            variant="soft"
            color="success"
            size="xSmall"
          />
        </Stack>
      </Paper>
      <Stack direction="row" spacing={1} sx={{ justifyContent: 'center', color: 'text.secondary' }}>
        <LockOutlined fontSize="small" />
        <Typography variant="caption">
          This download is audited and the temporary file expires automatically.
        </Typography>
      </Stack>
    </Stack>
  );
}

function EmptySearchResult({ label }: { label: string }) {
  return (
    <Stack sx={{ alignItems: 'center', py: 4, color: 'text.secondary' }}>
      <Search sx={{ mb: 0.5 }} />
      <Typography variant="body2">{label}</Typography>
    </Stack>
  );
}
